/**
 * Soft hyphen, zero-width space and word joiner, written as escapes because they are invisible in
 * source. Typesetting inserts them mid-word, where the reader never sees them but they split every
 * host, address and number the detector looks for. A non-breaking space is deliberately absent: it
 * is a visible separator the phone rules rely on.
 */
const invisibleCharRegex = /[\xAD\u200B\u2060]/;
const invisibleCharGlobalRegex = new RegExp(invisibleCharRegex, 'g');

interface NormalizedText {
    /** Source text with every invisible character removed. */
    clean: string;
    /**
     * Original-string offset of a clean-string offset. An exclusive end maps as
     * `toOriginal(end - 1) + 1` — mapping the exclusive index itself would either read past the
     * offsets or pull the invisible characters that follow the match into the range.
     */
    toOriginal(cleanIndex: number): number;
}

/** Strips the invisible characters from `text` and keeps the offsets that map the result back. */
function normalizeInvisibleChars(text: string): NormalizedText {
    if (!invisibleCharRegex.test(text)) {
        return { clean: text, toOriginal: cleanIndex => cleanIndex };
    }

    // Ascending clean-string offsets at which one more removed character starts to precede the rest:
    // a removal at source offset `p` after `k` earlier removals shifts clean offsets from `p - k` on.
    const removalOffsets: number[] = [];
    invisibleCharGlobalRegex.lastIndex = 0;

    let removal = invisibleCharGlobalRegex.exec(text);
    while (removal !== null) {
        removalOffsets.push(removal.index - removalOffsets.length);
        removal = invisibleCharGlobalRegex.exec(text);
    }

    const clean = text.replace(invisibleCharGlobalRegex, '');

    return {
        clean,
        toOriginal: cleanIndex => {
            let low = 0;
            let high = removalOffsets.length;

            while (low < high) {
                const middle = (low + high) >> 1;
                if (removalOffsets[middle] <= cleanIndex) {
                    low = middle + 1;
                } else {
                    high = middle;
                }
            }

            return cleanIndex + low;
        },
    };
}

export { normalizeInvisibleChars };
export type { NormalizedText };
